import {
    IonButton,
    IonCard,
    IonCardContent,
    IonContent,
    IonFooter,
    IonIcon,
    IonItem,
    IonLabel,
    IonPage,
    IonText,
    useIonRouter,
} from "@ionic/react";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { HeaderComponent } from "../../components";
import AppleImage from "../../assets/images/watches/watchOS.png";
import GoogleImage from "../../assets/images/watches/wearOS.png";
import SamsungImage from "../../assets/images/watches/tizen.png";
import GarminImage from "../../assets/images/watches/garmin.png";
import "./onBoarding.css";
import { RootState } from "../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { routes } from "../../route/constants";
import { setShowOnBoardingFlow } from "../../redux/actions/appStatus";
import { GarminWatchStatus } from "../../components/GarminWatchStatus";
import { isIos } from "../../services/platformDetection";
import { OnBoardingWatchRender } from "./onBoardingWatchRender";
import { take } from "ramda";

type AppInstallMessageProps = {
    icon: string;
    iconColor?: string;
    message: string;
};

const AppInstallMessage: FC<AppInstallMessageProps> = ({
    icon,
    iconColor,
    message,
}) => {
    return (
        <IonCard
            style={{
                marginTop: 20,
                animationDelay: "1.5s",
            }}
            className="fade-in-card"
        >
            <IonCardContent
                style={{
                    padding: 5,
                }}
            >
                <IonItem
                    style={{
                        fontSize: 18,
                        "--background": "transparent",
                    }}
                    lines="none"
                >
                    <IonIcon
                        aria-hidden="true"
                        icon={icon}
                        slot="start"
                        color={iconColor}
                        style={{
                            fontSize: 35,
                        }}
                    ></IonIcon>
                    <IonLabel>{message}</IonLabel>
                </IonItem>
            </IonCardContent>
        </IonCard>
    );
};

export const OnboardingWatchFound: FC = () => {
    const { t } = useTranslation();
    const { watchPlatform, pairingMethod } = useParams<{
        watchPlatform: string;
        pairingMethod?: string;
    }>();
    const isWebPaired = pairingMethod === "web";
    const watchStatus = useSelector((state: RootState) => state.watchStatus);
    const settings = useSelector((state: RootState) => state.settings.settings);
    const isApple = watchPlatform === "apple";
    const route = useIonRouter();
    const dispatch = useDispatch();
    const selectedGarmin = useSelector(
        (state: RootState) => state.garminWatches?.selectedDevice
    );

    const [loadTime, setLoadTime] = useState(Date.now());

    const handleNextButtonClicked = () => {
        if (isApple) {
            // This full onboarding flow not implemented for wearOS yet
            route.push(
                routes.onBoardingShareLocationData,
                "forward",
                "replace"
            );
        } else if (selectedGarmin) {
            dispatch(setShowOnBoardingFlow(false));
            return route.push(routes.tabResults, "root", "replace");
        } else {
            dispatch(setShowOnBoardingFlow(false));
            return route.push(routes.tabResults, "root", "replace");
        }
    };

    const hasWebWatch = () => {
        return settings.watches && settings.watches.length > 0;
    };

    const getImage = (makeOrPlatform: string) => {
        switch (makeOrPlatform.toLowerCase()) {
            case "watchos":
            case "apple":
                return AppleImage;
            case "tizen":
                return SamsungImage;
            case "garmin":
                return GarminImage;
            case "samsung":
            case "wearos":
                return GoogleImage;
            default:
                return GoogleImage;
        }
    };

    const getWatchText = () => {
        if (!isWebPaired && watchStatus.isPaired && watchStatus.model) {
            return watchStatus.model;
        } else if (!isWebPaired && selectedGarmin) {
            return selectedGarmin.friendlyName;
        } else if (
            isWebPaired &&
            settings.watches &&
            settings.watches.length > 0
        ) {
            const watch = settings.watches[settings.watches.length - 1];
            return watch.make ? `${watch.make} ${watch.model}` : watch.model;
        }
        return "";
    };

    const isDeviceSetupComplete = () => {
        return selectedGarmin?.hasAppInstalled && selectedGarmin.isBTCompatible;
    };

    const isTakingTooLong = () => {
        return Date.now() - loadTime > 30 * 1000;
    };

    const shouldShowNextButton = () => {
        return watchPlatform === "garmin" && !selectedGarmin ? false : true;
    };

    const shouldNextButtonBeDisabled = () => {
        return !!(
            watchPlatform === "garmin" &&
            selectedGarmin &&
            !isDeviceSetupComplete() &&
            !isTakingTooLong()
        );
    };

    return (
        <IonPage>
            <HeaderComponent
                title={t("help.onBoarding.step0a")}
                titleTestId="onboarding-watch-found"
                showProgressBar={true}
                progressValue={0.7}
            />
            <IonContent>
                <h2
                    className="text-weight-bold text-large align-center uppercase"
                    style={{
                        width: "100%",
                        marginTop: 20,
                        fontWeight: 400,
                        fontSize: 14,
                    }}
                >
                    {t("help.onBoarding.foundWatch")}
                </h2>

                <IonCard color="transparent">
                    <IonCard>
                        <IonItem lines="none">
                            <div
                                style={{
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "10px 0",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "center",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <div>
                                            <img
                                                src={getImage(
                                                    watchStatus.make ||
                                                        watchPlatform
                                                )}
                                                height={15}
                                                alt="watch image"
                                            />
                                        </div>

                                        <IonText
                                            style={{
                                                fontSize: 16,
                                            }}
                                            hidden={!getWatchText()}
                                        >
                                            {getWatchText()}
                                        </IonText>
                                    </div>
                                </div>
                                <div>
                                    <IonText color="primary">
                                        {t(
                                            "help.onBoarding.paired"
                                        ).toUpperCase()}
                                    </IonText>
                                </div>
                            </div>
                        </IonItem>
                    </IonCard>
                    {watchPlatform !== "garmin" && (
                        <OnBoardingWatchRender
                            imageSrc={getImage(
                                watchStatus.make || watchPlatform
                            )}
                        />
                    )}
                </IonCard>

                <div className="fade-in-card" style={{ animationDelay: "1s" }}>
                    <GarminWatchStatus
                        device={selectedGarmin}
                    ></GarminWatchStatus>
                </div>
            </IonContent>
            <IonFooter>
                {/* MAYBE LATER */}
                {watchPlatform === "garmin" && !isTakingTooLong() && (
                    <IonButton
                        fill="clear"
                        expand={"full"}
                        onClick={() => handleNextButtonClicked()}
                        className={"fade-in-card"}
                        style={{
                            animationDelay: "5s",
                        }}
                    >
                        {t("general.maybeLater")}
                    </IonButton>
                )}

                {/* Taking too long - can skip */}
                {watchPlatform === "garmin" &&
                    isTakingTooLong() &&
                    !selectedGarmin?.hasAppInstalled && (
                        <p style={{ margin: 0, padding: 10 }}>
                            {t("garminWatches.canProceedWhilstInstalling")}
                        </p>
                    )}

                {/* NEXT BUTTON */}
                {shouldShowNextButton() && (
                    <IonButton
                        expand="block"
                        onClick={() => handleNextButtonClicked()}
                        data-testid="onboarding-watch-found-next-button"
                        disabled={shouldNextButtonBeDisabled()}
                        style={
                            selectedGarmin
                                ? {
                                      animationDelay: "4s",
                                  }
                                : { animationDelay: "0s" }
                        }
                        className={
                            selectedGarmin && shouldNextButtonBeDisabled()
                                ? "fade-in-button-disabled"
                                : ""
                        }
                    >
                        {selectedGarmin && isTakingTooLong()
                            ? t("general.skip")
                            : t("general.next")}
                    </IonButton>
                )}
            </IonFooter>
        </IonPage>
    );
};
